import type { Column, Row } from "@tanstack/react-table"

/**
 * Group total computation, shared by the grid's totals row and the Excel export so the two can
 * never disagree. Values only — formatting and markup stay with the caller, because the grid wants
 * a formatted string and the spreadsheet wants a real number it can format itself.
 */

// excludeFromGroupTotal opts out a column that is numeric but not additive: a rate, a per-day
// figure, or an id or count that would double-count across members. Being currency-typed is not the
// same as being summable.
export function isAdditiveColumn(meta: any) {
  return (
    !meta?.excludeFromGroupTotal &&
    (meta?.type === "currency" || meta?.type === "number" || meta?.type === "integer")
  )
}

/**
 * Which field on the row holds a money column's currency. A report whose money is all in one
 * currency says nothing and gets `currency`; one carrying a bill side and a pay side that may
 * differ names the field per column, so Pay Amount is not labelled with the bill currency.
 */
const DEFAULT_CURRENCY_FIELD = "currency"

export function currencyFieldOf(meta: any): string {
  return meta?.currencyField ?? DEFAULT_CURRENCY_FIELD
}

/**
 * How a group names itself. Rows with no value for the grouped field bucket together, and that
 * bucket needs naming contextually - "(No Branch)" rather than a blank, a dash or "null". The value
 * can arrive as null, as an empty string, or as the literal text "null" / "undefined" from a synced
 * field, so all four have to be treated the same way.
 *
 * @param columnHeader the grouped column's header, used to name the empty bucket.
 */
export function groupDisplayLabel(groupingValue: unknown, columnHeader: unknown): string {
  const columnName = typeof columnHeader === "string" ? columnHeader : "value"
  const asText = groupingValue == null ? "" : String(groupingValue).trim()
  const hasValue =
    asText !== "" && asText.toLowerCase() !== "null" && asText.toLowerCase() !== "undefined"
  return hasValue ? String(groupingValue) : `(No ${columnName})`
}

export interface GroupTotals {
  /** Column id → total. A column with no total is absent; null means deliberately blank. */
  values: Map<string, number | null>
  /**
   * Column id → the single currency that column's rows agree on, for additive money columns.
   * Undefined where they do not agree, or where the rows carry no currency at all.
   *
   * Per column rather than per group: a report can carry a bill currency and a pay currency that
   * differ, in which case one column's rows can agree while another's do not.
   */
  currency: Map<string, string | undefined>
  /**
   * Column ids whose rows span more than one currency, so that column's sum is not meaningful.
   * A column absent from this set either agrees on a currency or has none to compare.
   */
  mixedCurrency: Set<string>
}

/**
 * getLeafRows is a misnomer: it flattens the whole subtree and pushes EVERY node, intermediate
 * group rows included. A grouped row answers getValue() with TanStack's own aggregate, so leaving
 * them in counts each amount twice - once through the subgroup's aggregate and once through the
 * rows behind it - and a roll-up total came out at exactly double the truth as soon as a second
 * grouping was added.
 */
export function dataLeavesOf<T>(group: Row<T>): Row<T>[] {
  return group.getLeafRows().filter((r) => !r.getIsGrouped())
}

export function computeGroupTotals<T>(group: Row<T>, columns: Column<T, any>[]): GroupTotals {
  const leaves = dataLeavesOf(group)

  const sumOverGroup = (columnId: string) =>
    leaves.reduce<number | null>((acc, leaf) => {
      const v = leaf.getValue(columnId)
      // A withheld figure is not zero. The Finance report withholds a money column where the streams
      // behind it disagree on a currency, and adding up the rows either side of one would present a
      // short total as a whole one, with nothing on the row saying so. The currency guard does not
      // catch it: the withheld row still carries its engagement currency, so the group agrees on one
      // and the total renders labelled rather than as "Mixed currency".
      if (acc == null || v == null) {
        return null
      }
      return acc + (typeof v === "number" ? v : 0)
    }, 0)

  // Amounts are only summable within one currency, and a group can legitimately mix them - in which
  // case adding the figures and stamping one member's symbol on the result would be wrong.
  //
  // Read off the row by the column's own currency field, so a report carrying both a bill and a pay
  // currency is judged per column: Bill Amount can agree across the group while Pay Amount does not.
  const currencyOfField = (() => {
    // Memoised by field name. Finance has 16 additive currency columns and every one of them reads
    // `currency`, so without this the group's leaves are walked 16 times over for one answer - on
    // every render of the totals row, and again per group on export.
    const cache = new Map<string, { single?: string; mixed: boolean }>()

    return (field: string) => {
      const hit = cache.get(field)
      if (hit) {
        return hit
      }

      const codes = leaves
        .map((leaf) => (leaf.original as any)?.[field])
        .filter(Boolean) as string[]
      const distinct = new Set(codes)

      const found = {
        // Every leaf has to carry a currency AND agree on it. Agreement among only those that have
        // one is not enough: a group of [CAD, null] knows the currency of half its money, and
        // stamping CAD on the total asserts it over the other half. Withholding the symbol is not
        // the same as suppressing the total, which stays either way - a group carrying no currency
        // at all is the norm on a report that supplies none, and blanking those figures would be the
        // wrong trade.
        single: distinct.size === 1 && codes.length === leaves.length ? codes[0] : undefined,
        // Two known currencies in one sum, which no symbol can describe.
        mixed: distinct.size > 1,
      }

      cache.set(field, found)
      return found
    }
  })()

  const values = new Map<string, number | null>()
  const currency = new Map<string, string | undefined>()
  const mixedCurrency = new Set<string>()

  for (const column of columns) {
    const meta = (column.columnDef.meta as any) ?? {}
    if (isAdditiveColumn(meta)) {
      values.set(column.id, sumOverGroup(column.id))
      if (meta.type === "currency") {
        const found = currencyOfField(currencyFieldOf(meta))
        currency.set(column.id, found.single)
        if (found.mixed) {
          mixedCurrency.add(column.id)
        }
      }
    } else if (meta.groupTotalRatio) {
      // A percentage cannot be summed, but it CAN be recomputed from the summed parts it was
      // derived from — which the column has to name, since nothing here could infer them. Same rule
      // engagement.spEngagementTable applies to its own derived columns: recompute from the sums,
      // never add the ratios.
      const { numerator, denominator, scale = 100 } = meta.groupTotalRatio
      const den = sumOverGroup(denominator)
      const num = sumOverGroup(numerator)
      values.set(column.id, num == null || den == null || den === 0 ? null : (num / den) * scale)
    }
  }

  return {
    values,
    currency,
    mixedCurrency,
  }
}
